import { useEffect, useMemo, useState } from "react";
import { Loader2, ArrowRight } from "lucide-react";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { supabase } from "@/integrations/supabase/client";
import { getPaddleEnvironment } from "@/lib/paddle";
import { useSubscription } from "@/hooks/useSubscription";
import { toast } from "sonner";

const PLAN_OPTIONS = [
  { id: "solo_monthly",       plan: "solo",       cycle: "monthly", label: "Solo · Monthly",       price: "$29 / mo" },
  { id: "solo_yearly",        plan: "solo",       cycle: "yearly",  label: "Solo · Yearly",        price: "$290 / yr" },
  { id: "dealership_monthly", plan: "dealership", cycle: "monthly", label: "Dealership · Monthly", price: "$49 / seat / mo" },
  { id: "dealership_yearly",  plan: "dealership", cycle: "yearly",  label: "Dealership · Yearly",  price: "$490 / seat / yr" },
];

function formatMoney(amountStr: string | null | undefined, currency: string | null | undefined) {
  if (!amountStr || amountStr === "0") return "$0.00";
  const cents = Number(amountStr);
  if (!Number.isFinite(cents)) return amountStr;
  try {
    return new Intl.NumberFormat(undefined, { style: "currency", currency: currency ?? "USD" }).format(cents / 100);
  } catch {
    return `${(cents / 100).toFixed(2)} ${currency ?? ""}`;
  }
}

interface Preview {
  immediateAmount: string | null;
  immediateCredit: string | null;
  currency: string | null;
  nextBilledAt: string | null;
  nextAmount: string | null;
}

export function PlanSwitcherDialog({ open, onOpenChange }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { subscription, refresh } = useSubscription();
  const currentPriceId = subscription?.price_id ?? "";
  const [selectedPriceId, setSelectedPriceId] = useState(currentPriceId);
  const [seats, setSeats] = useState(subscription?.quantity ?? 1);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [previewing, setPreviewing] = useState(false);
  const [applying, setApplying] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open) {
      setSelectedPriceId(currentPriceId);
      setSeats(subscription?.quantity ?? 1);
      setPreview(null);
      setError(null);
    }
  }, [open, currentPriceId, subscription?.quantity]);

  const isDealership = selectedPriceId.startsWith("dealership_");
  const quantity = isDealership ? Math.max(1, Math.min(100, seats)) : 1;
  const noChange = selectedPriceId === currentPriceId && quantity === (subscription?.quantity ?? 1);

  // Debounced preview when selection changes
  useEffect(() => {
    if (!open || noChange) { setPreview(null); return; }
    const t = setTimeout(async () => {
      setPreviewing(true);
      setError(null);
      const { data, error: err } = await supabase.functions.invoke("subscription-change-preview", {
        body: {
          priceId: selectedPriceId,
          quantity,
          environment: getPaddleEnvironment(),
        },
      });
      setPreviewing(false);
      if (err || data?.error) {
        setError(data?.error ?? err?.message ?? "Could not preview");
        setPreview(null);
        return;
      }
      setPreview(data as Preview);
    }, 350);
    return () => clearTimeout(t);
  }, [open, selectedPriceId, quantity, noChange]);

  const apply = async () => {
    setApplying(true);
    const { error: err, data } = await supabase.functions.invoke("subscription-change-apply", {
      body: { priceId: selectedPriceId, quantity, environment: getPaddleEnvironment() },
    });
    setApplying(false);
    if (err || data?.error) {
      toast.error("Plan change failed", { description: data?.error ?? err?.message });
      return;
    }
    toast.success("Plan updated", { description: "Your new plan is active." });
    await refresh();
    onOpenChange(false);
  };

  const currentLabel = useMemo(
    () => PLAN_OPTIONS.find((o) => o.id === currentPriceId)?.label ?? currentPriceId,
    [currentPriceId],
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Switch plan</DialogTitle>
          <DialogDescription>
            Currently on <span className="font-medium">{currentLabel}</span>. Changes are prorated immediately by Paddle.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <RadioGroup value={selectedPriceId} onValueChange={setSelectedPriceId} className="space-y-1.5">
            {PLAN_OPTIONS.map((opt) => (
              <label
                key={opt.id}
                htmlFor={opt.id}
                className={`flex items-center justify-between gap-3 rounded-md border p-3 cursor-pointer transition-colors ${
                  selectedPriceId === opt.id ? "border-primary bg-primary/5" : "border-border hover:border-border/80"
                }`}
              >
                <div className="flex items-center gap-3">
                  <RadioGroupItem value={opt.id} id={opt.id} />
                  <span className="text-sm">{opt.label}</span>
                </div>
                <span className="text-sm font-medium text-muted-foreground">{opt.price}</span>
              </label>
            ))}
          </RadioGroup>

          {isDealership && (
            <div className="space-y-1.5">
              <Label htmlFor="seats" className="text-xs">Seats</Label>
              <Input
                id="seats"
                type="number"
                min={1}
                max={100}
                value={seats}
                onChange={(e) => setSeats(parseInt(e.target.value) || 1)}
                className="h-9 w-24"
              />
            </div>
          )}

          {/* Preview panel */}
          <div className="rounded-md border border-border bg-muted/30 p-3 text-sm">
            {noChange ? (
              <p className="text-muted-foreground">Pick a different plan or seat count to see the impact.</p>
            ) : previewing ? (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" /> Calculating proration…
              </div>
            ) : error ? (
              <p className="text-destructive text-xs">{error}</p>
            ) : preview ? (
              <div className="space-y-1.5">
                <div className="flex items-center justify-between">
                  <span className="text-muted-foreground">Charged today (prorated)</span>
                  <span className="font-medium">{formatMoney(preview.immediateAmount, preview.currency)}</span>
                </div>
                {preview.nextAmount && (
                  <div className="flex items-center justify-between">
                    <span className="text-muted-foreground">
                      Next charge{preview.nextBilledAt ? ` · ${new Date(preview.nextBilledAt).toLocaleDateString()}` : ""}
                    </span>
                    <span className="font-medium">{formatMoney(preview.nextAmount, preview.currency)}</span>
                  </div>
                )}
              </div>
            ) : null}
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={applying}>Cancel</Button>
          <Button onClick={apply} disabled={noChange || applying || previewing || !!error}>
            {applying
              ? <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              : <ArrowRight className="h-4 w-4 mr-2" />}
            Confirm switch
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}